/**
 * Confirm Modal Component
 * Reusable confirmation dialog to replace browser window.confirm()
 * Provides consistent styling with the rest of the dashboard
 */

import React from 'react';
import Modal from './Modal';
import Button from './Button';

/**
 * ConfirmModal Component
 *
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether modal is open
 * @param {Function} props.onClose - Close handler (called on cancel)
 * @param {Function} props.onConfirm - Confirm handler
 * @param {string} props.title - Modal title
 * @param {string} props.message - Main message to display
 * @param {string} props.details - Optional additional details (smaller text)
 * @param {string} props.confirmLabel - Label for confirm button (default: 'Confirm')
 * @param {string} props.cancelLabel - Label for cancel button (default: 'Cancel')
 * @param {string} props.variant - Button variant: 'danger', 'warning', 'primary' (default: 'primary')
 * @param {boolean} props.loading - Whether action is in progress
 */
const ConfirmModal = ({
    isOpen,
    onClose,
    onConfirm,
    title = 'Confirm Action',
    message,
    details,
    confirmLabel = 'Confirm',
    cancelLabel = 'Cancel',
    variant = 'primary',
    loading = false
}) => {
    if (!isOpen) return null;

    const handleConfirm = () => {
        if (!loading) {
            onConfirm();
        }
    };

    const handleCancel = () => {
        if (!loading) {
            onClose();
        }
    };

    // Get icon and color based on variant
    const getVariantStyles = () => {
        switch (variant) {
            case 'danger':
                return {
                    iconColor: '#e74c3c',
                    icon: '!'
                };
            case 'warning':
                return {
                    iconColor: '#f39c12',
                    icon: '!'
                };
            default:
                return {
                    iconColor: '#3498db',
                    icon: '?'
                };
        }
    };

    const variantStyles = getVariantStyles();

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleCancel}
            title={title}
            maxWidth="450px"
            zIndex={1200}
            footer={
                <>
                    {cancelLabel && (
                        <Button
                            onClick={handleCancel}
                            variant="secondary"
                            disabled={loading}
                        >
                            {cancelLabel}
                        </Button>
                    )}
                    <Button
                        onClick={handleConfirm}
                        variant={variant}
                        loading={loading}
                    >
                        {loading ? 'Processing...' : confirmLabel}
                    </Button>
                </>
            }
        >
            <div style={{
                padding: '0.5rem 0'
            }}>
                {/* Message */}
                <p style={{
                    fontSize: '1rem',
                    color: 'var(--fg-primary)',
                    margin: '0 0 0.5rem 0',
                    lineHeight: '1.6'
                }}>
                    {message}
                </p>

                {/* Details */}
                {details && (
                    <p style={{
                        fontSize: '0.875rem',
                        color: '#a0aec0',
                        margin: '0.75rem 0 0 0',
                        lineHeight: '1.5'
                    }}>
                        {details}
                    </p>
                )}
            </div>
        </Modal>
    );
};

export default ConfirmModal;

